import { useEffect } from 'react';

import { useDeviceSettingsApi } from '~Components/DeviceSettingsUniverse';
import { StorageData, useStorage } from '~Components/Storage';
import { usePrinterApi, useSelector } from '~Hooks';
import { useDeviceHandler } from '.';
import _ from 'lodash';
import { AvailablePrinter } from '~reducers';

/**
 * Migration Manager
 *
 * Used for keeping the client and backend in sync when there's client-side
 * changes.
 */
const MigrationManager = () => {
  const printerApi = usePrinterApi();
  const deviceHandler = useDeviceHandler();
  const [storage, update, loading] = useStorage();
  const deviceSettingsApi = useDeviceSettingsApi();
  const deviceEvents = useSelector((state) => state.deviceSettingsEvents);
  const deviceSnapshot = useSelector((state) => state.deviceSettingsSnapshot);

  // Dont check a bunch of settings before our local device is loaded
  const hasValidDevice =
    deviceHandler.universe && (deviceEvents.length > 0 || deviceSnapshot.snapshot);

  const printers = useSelector((state) => state.printers);
  const availablePrinters = useSelector((state) => state.availablePrinters);

  useEffect(() => {
    if (loading || !deviceHandler.universe || !hasValidDevice || !storage.deviceId) {
      return;
    }

    /**
     * Iterate all fields in storage and update device settings if
     * they are different.
     */
    for (const key of Object.keys(storage)) {
      const storageValue = storage[key as keyof StorageData];
      const deviceValue = deviceHandler.universe.monitoring.storage[key as keyof StorageData];

      if (!_.isEqual(storageValue, deviceValue)) {
        deviceSettingsApi.monitoring.setStorageField(key, storageValue);
      }
    }
  }, [storage, deviceHandler.universe, loading]);

  useEffect(() => {
    /** Ensure no values gets set with undefined (this can happen if a remotely controlled setting gets removed) */
    const remotelyControlledData = Object.entries(deviceHandler.universe?.storage ?? {})
      .filter(([k, v]) => v !== undefined && k !== 'clientId')
      .reduce((acc, [k, v]) => {
        acc[k] = v;
        return acc;
      }, {} as Record<string, unknown>);

    /** For each setting retrieved from the backoffice, make sure localStorage gets it too so we can safely remove the backoffice control */
    for (const key of Object.keys(remotelyControlledData) as (keyof StorageData)[]) {
      update({ [key]: remotelyControlledData[key] });
    }
  }, [deviceHandler.universe?.storage]);

  /**
   * Update printers and available printers whenever they change
   */
  useEffect(() => {
    if (!deviceHandler.universe || printerApi.loading || !hasValidDevice) {
      return;
    }

    const mappedPrinters = printers.map((p) => ({
      id: p.id,
      stations: p.stations,
    }));

    const printersOnDevice = deviceHandler.universe.monitoring.printers.map((p) => ({
      id: p.id,
      stations: p.stations,
    }));

    const availablePrintersOnDevice: AvailablePrinter[] = deviceHandler.universe.monitoring.availablePrinters.map(
      (printer) => ({
        id: printer.id,
        model: printer.model,
        type: printer.model,
      }),
    );

    /**
     * If there's a difference between the printers on the device and the
     * printers in the client, update the device.
     */
    if (!_.isEqual(mappedPrinters, printersOnDevice)) {
      deviceSettingsApi.monitoring.setPrinters(
        printers.map((p) => ({
          id: p.id,
          name: printerApi.getNameFromId(p.id) || '',
          model: p.model,
          primary: p.primary,
          stations: p.stations,
        })),
      );
    }

    /**
     * If there's a difference between the available printers on the device
     * and the available printers in the client, update the device.
    
    DISABLED (Joar): The data format doesnt match, so this will always be true. Commented out until we can fix this.
    
    if (!_.isEqual(availablePrinters, availablePrintersOnDevice)) {
      deviceSettingsApi.monitoring.setAvailablePrinters(
        availablePrinters.map((p) => ({
          id: p.id,
          name: printerApi.getNameFromId(p.id) || '',
          model: p.model,
        })),
      );
    }
     */
  }, [printers, availablePrinters, deviceHandler.universe, printerApi.loading]);

  return null;
};

export default MigrationManager;
